import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import logo from "@/assets/logo.png";

interface HeroSectionProps {
  user: any;
  onGetStarted: () => void;
}

export const HeroSection = ({ user, onGetStarted }: HeroSectionProps) => {
  return (
    <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
      {/* Warm gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-background" />
      
      {/* Subtle paper texture overlay */}
      <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbHRlcj0idXJsKCNhKSIgb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')]" />
      
      {/* Prosperity glow - warm gold accent */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-accent/10 via-accent/5 to-transparent rounded-full blur-3xl" />
      
      {/* Primary teal glow */}
      <div className="absolute bottom-1/4 right-[15%] w-80 h-80 bg-primary/10 rounded-full blur-[100px]" />

      <div className="container relative z-10 px-4 py-16 md:py-24">
        <div className="max-w-5xl mx-auto text-center space-y-6 md:space-y-8">
          {/* Logo with golden glow */}
          <div className="flex justify-center mb-6 md:mb-10 animate-slide-up" style={{ animationDelay: '0.1s', opacity: 0 }}>
            <div className="relative">
              <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full scale-150" />
              <img 
                src={logo} 
                alt="KOSH" 
                className="relative h-24 w-24 md:h-36 md:w-36 drop-shadow-2xl animate-float" 
                fetchPriority="high" 
                loading="eager" 
                width={144} 
                height={144} 
              />
            </div>
          </div>

          {/* Cultural badge */}
          <div className="animate-slide-up" style={{ animationDelay: '0.2s', opacity: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20 backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
              </span>
              <span className="text-xs md:text-sm font-medium text-foreground/80">
                Made for Indian Small Businesses
              </span>
            </div>
          </div>

          {/* Main Headline - Hindi-English code-switch */}
          <div className="space-y-4 animate-slide-up" style={{ animationDelay: '0.3s', opacity: 0 }}>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold leading-[1.0] tracking-tight">
              <span className="text-foreground">Hisaab.</span>
              <br />
              <span className="text-gradient">Sorted.</span>
            </h1>
          </div>

          {/* Subheadline - Clear value proposition */}
          <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed animate-slide-up px-4" style={{ animationDelay: '0.4s', opacity: 0 }}>
            Stop guessing. Start knowing.
            <br className="hidden sm:block" />
            <span className="text-foreground font-medium">Track your daily sales, expenses, and profit in 60 seconds.</span>
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-4 md:pt-8 animate-slide-up px-4" style={{ animationDelay: '0.5s', opacity: 0 }}>
            <Button 
              size="lg" 
              onClick={onGetStarted}
              className="group text-base md:text-lg h-14 md:h-16 px-10 md:px-12 rounded-full bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary shadow-xl shadow-primary/25 hover:shadow-2xl hover:shadow-primary/35 transition-all duration-300 w-full sm:w-auto"
            >
              {user ? "Go to Dashboard" : "Start My Free Account"}
              <ArrowRight className="ml-2 h-5 w-5 md:h-6 md:w-6 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => document.getElementById('ritual')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-base md:text-lg h-14 md:h-16 px-10 md:px-12 rounded-full border-2 border-border hover:border-primary/50 hover:bg-primary/5 transition-all duration-300 w-full sm:w-auto"
            >
              See How It Works
            </Button>
          </div>

          {/* Trust indicators - culturally relevant */}
          <div className="pt-8 md:pt-12 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-xs md:text-sm text-muted-foreground animate-slide-up" style={{ animationDelay: '0.6s', opacity: 0 }}>
            {[
              "No credit card needed",
              "Works in Hindi & English"
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-success/20 flex items-center justify-center">
                  <svg className="w-2.5 h-2.5 md:w-3 md:h-3 text-success" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stats - warm styling */}
        <div className="max-w-3xl mx-auto mt-16 md:mt-20 animate-slide-up" style={{ animationDelay: '0.7s', opacity: 0 }}>
          <div className="grid grid-cols-3 gap-3 md:gap-6">
            {[
              { value: "60", unit: "sec", label: "Daily Close" },
              { value: "₹0", unit: "", label: "Forever Free" },
              { value: "24/7", unit: "", label: "AI Assistant" },
            ].map((stat, i) => (
              <div key={i} className="text-center p-4 md:p-6 rounded-2xl glass border border-border/50 hover:border-accent/30 transition-colors">
                <div className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-gradient mb-1">
                  {stat.value}<span className="text-lg md:text-xl">{stat.unit}</span>
                </div>
                <div className="text-xs md:text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
          <div className="w-1 h-2 rounded-full bg-muted-foreground/50" />
        </div>
      </div>
    </section>
  );
};
